import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import usePolling from '@/hooks/usePolling';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AbsenceAlerts = ({ showCheckbox = false, title = "Alertas de Inasistencias" }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedAlert, setExpandedAlert] = useState(null);
  const [detailData, setDetailData] = useState({});

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/absence-alerts`, { headers });
      setAlerts(res.data);
    } catch (e) {
      console.error('Error fetching alerts:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  // Real-time auto-refresh so new alerts appear without manual reload.
  usePolling(fetchAlerts, { intervalMs: 0 });

  const toggleExpand = async (alertId) => {
    if (expandedAlert === alertId) {
      setExpandedAlert(null);
      return;
    }
    setExpandedAlert(alertId);
    if (!detailData[alertId]) {
      try {
        const res = await axios.get(`${API}/absence-alerts/${alertId}/detail`, { headers });
        setDetailData(prev => ({ ...prev, [alertId]: res.data }));
      } catch (e) {
        console.error('Error fetching detail:', e);
      }
    }
  };

  const markAttended = async (alertId) => {
    if (!window.confirm('¿Marcar como atendido? Se eliminará esta alerta.')) return;
    try {
      await axios.delete(`${API}/absence-alerts/${alertId}/attend`, { headers });
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch (e) {
      console.error('Error:', e);
    }
  };

  if (loading) return <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-red-400" /></div>;
  if (alerts.length === 0) return null;

  const pending = alerts.filter(a => !a.attended);
  const attended = alerts.filter(a => a.attended);

  // If no pending alerts and we don't show attended list, hide entirely
  if (pending.length === 0 && !showCheckbox) return null;

  return (
    <div className="space-y-4" data-testid="absence-alerts-section">
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-5 h-5 text-red-500" />
        <h2 className="text-lg font-bold text-slate-900">{title}</h2>
        {pending.length > 0 && (
          <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{pending.length}</span>
        )}
      </div>

      {pending.map(alert => (
        <Card key={alert.id} className="border-l-4 border-l-red-500 overflow-hidden" data-testid={`alert-${alert.id}`}>
          <div className="p-4 cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => toggleExpand(alert.id)}>
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-red-600">{alert.is_recurrence ? 'REINCIDENCIA' : 'ALERTA'}</span>
                  <span className="text-sm text-slate-600">|</span>
                  <span className="font-semibold text-slate-900">{alert.student_name}</span>
                </div>
                <p className="text-sm text-slate-600 mt-0.5">
                  {alert.absence_count} faltas en <span className="font-medium">{alert.triggering_class_subject}</span> ({alert.triggering_class_group})
                </p>
                <p className="text-xs text-slate-400 mt-0.5">
                  {new Date(alert.triggered_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {showCheckbox && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-green-600 border-green-200 hover:bg-green-50"
                    onClick={(e) => { e.stopPropagation(); markAttended(alert.id); }}
                    data-testid={`attend-alert-${alert.id}`}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Atendido
                  </Button>
                )}
                {expandedAlert === alert.id ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
              </div>
            </div>
          </div>
          {expandedAlert === alert.id && detailData[alert.id] && (
            <div className="border-t bg-slate-50 p-4">
              <p className="text-xs font-semibold text-slate-500 mb-2 uppercase">Detalle por materia</p>
              <div className="space-y-2">
                {detailData[alert.id].class_details.map(cd => (
                  <div key={cd.class_id} className={`flex items-center justify-between p-2 rounded ${cd.is_trigger ? 'bg-red-50 border border-red-200' : 'bg-white border'}`}>
                    <div>
                      <span className="font-medium text-sm">{cd.subject}</span>
                      {cd.is_trigger && <span className="ml-2 text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-bold">ORIGEN</span>}
                      <p className="text-xs text-slate-500">Prof. {cd.teacher_name}</p>
                    </div>
                    <div className="flex gap-4 text-xs">
                      <span className={cd.absences >= 5 ? 'text-red-600 font-bold' : 'text-slate-600'}>
                        Faltas: {cd.absences}/{cd.total_classes}
                      </span>
                      <span className="text-slate-600">
                        Tareas: {cd.tasks_completed}/{cd.total_tasks}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      ))}

      {showCheckbox && attended.length > 0 && (
        <div className="mt-6">
          <p className="text-sm font-semibold text-slate-500 mb-2">Casos atendidos ({attended.length})</p>
          <div className="space-y-2">
            {attended.map(alert => (
              <Card key={alert.id} className="border-l-4 border-l-green-400 p-3 opacity-70" data-testid={`alert-attended-${alert.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium text-sm text-slate-700">{alert.student_name}</span>
                    <span className="text-xs text-slate-500 ml-2">{alert.triggering_class_subject} - {alert.absence_count} faltas</span>
                    {alert.is_recurrence && <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-bold">REINCIDENCIA</span>}
                  </div>
                  <div className="flex items-center gap-1 text-green-600 text-xs">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Atendido {alert.attended_at ? new Date(alert.attended_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' }) : ''}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AbsenceAlerts;
